import varianceAnalysisConstants from "../constants";
import { storeGet } from '@/store/store-utils';

const { STATUS, MESSAGES } = varianceAnalysisConstants;

const DEFAULT_NODE = () => ({
  data: [],
  paginationPage: 0,
  hasMore: false,
  lastBatch: [],
  totalCount: 0,
  currentPage: 0,
});

const buildCacheKey = (filters = {}) => {
  const {
    cycletype = "all",
    role = "all",
    year = "all",
    period = "all",
    network = "all",
    region = "all",
    country = "all",
    type = "all",
  } = filters;
  return [cycletype, role, year, period, network, region, country, type].join("|");
};

export default {
  namespaced: true,

  state: () => ({
    cache: {},
    activeKey: null,
    loading: false,
    error: null,
    requestToken: 0,
    /** @type {AbortController|null} AbortController to cancel pending requests */
    abortController: null,
  }),

  mutations: {
    SET_LOADING(state, value) {
      state.loading = Boolean(value);
    },
    SET_ERROR(state, message) {
      state.error = message;
    },
    SET_ACTIVE_KEY(state, key) {
      state.activeKey = key;
    },
    SET_REQUEST_TOKEN(state, token) {
      state.requestToken = token;
    },
    /**
     * Sets the AbortController for the current request, cancelling any previous one
     * @param {Object} state - Vuex state
     * @param {AbortController|null} controller - The new AbortController or null to clear
     */
    SET_ABORT_CONTROLLER(state, controller) {
      // Cancel previous request if it exists
      if (state.abortController) {
        state.abortController.abort();
      }
      state.abortController = controller;
    },
    /**
     * Sets the total count for a specific cache key from the count API
     * @param {Object} state - Vuex state
     * @param {Object} payload - { key, totalCount }
     */
    SET_TOTAL_COUNT(state, { key, totalCount }) {
      if (!state.cache[key]) {
        state.cache[key] = DEFAULT_NODE();
      }
      state.cache[key].totalCount = Number(totalCount) || 0;
    },
    ENSURE_NODE(state, key) {
      if (!state.cache[key]) {
        state.cache[key] = DEFAULT_NODE();
      }
    },
    RESET_NODE(state, key) {
      state.cache[key] = DEFAULT_NODE();
    },
    REPLACE_NODE(state, { key, data = [], hasMore = false, pageIndex = 0, totalCount = 0 }) {
      state.cache[key] = {
        data: Array.isArray(data) ? [...data] : [],
        paginationPage:
          Array.isArray(data) && data.length > 0 ? (Number(pageIndex) || 0) + 1 : Number(pageIndex) || 0,
        hasMore: Boolean(hasMore),
        lastBatch: Array.isArray(data) ? [...data] : [],
        totalCount: Number(totalCount) || 0,
        currentPage: Number(pageIndex) || 0,
      };
    },
    APPEND_TO_NODE(state, { key, data = [], hasMore = false, totalCount = 0, pageIndex = null }) {
      const node = state.cache[key] || DEFAULT_NODE();
      const nextData = Array.isArray(data) ? data : [];
      const updatedData = [...(node.data || []), ...nextData];
      const resolvedPageIndex =
        typeof pageIndex === "number" ? pageIndex : Math.max((node.currentPage || 0) + (nextData.length > 0 ? 1 : 0), 0);
      state.cache[key] = {
        data: updatedData,
        paginationPage:
          (nextData.length > 0 ? resolvedPageIndex + 1 : resolvedPageIndex || node.paginationPage || 0),
        hasMore: Boolean(hasMore),
        lastBatch: [...nextData],
        totalCount: Number(totalCount) || node.totalCount || 0,
        currentPage: resolvedPageIndex,
      };
    },
    SET_LAST_BATCH(state, { key, batch, totalCount = null, pageIndex = null }) {
      const node = state.cache[key] || DEFAULT_NODE();
      state.cache[key] = {
        data: [...(node.data || [])],
        paginationPage: node.paginationPage || 0,
        hasMore: Boolean(node.hasMore),
        lastBatch: Array.isArray(batch) ? [...batch] : [],
        totalCount:
          totalCount !== null && totalCount !== undefined
            ? Number(totalCount) || 0
            : node.totalCount || 0,
        currentPage:
          pageIndex !== null && pageIndex !== undefined
            ? Number(pageIndex) || 0
            : node.currentPage || 0,
      };
    },
  },

  actions: {
    /**
     * Fetches total count of reporting units for pagination
     * Uses the dedicated count API endpoint
     * @param {Object} context - Vuex action context
     * @param {Object} payload - { axiosInstance, filters, signal }
     * @returns {Promise<number>} - Total count of records
     */
    async fetchTotalCount({ commit, rootState }, { axiosInstance, filters = {}, signal = null }) {
      const key = buildCacheKey(filters);
      try {
        const response = await storeGet(
          axiosInstance, rootState,
          "/v3/varianceanalysis/analysis/getreportingunit_pagination_count/",
          filters,
          signal ? { signal } : {}
        );

        if (!response || response.data?.status !== STATUS.SUCCESS) {
          throw new Error(
            response?.data?.message || MESSAGES.REPORTING_UNIT_FETCH_FAILED
          );
        }

        // Extract total count from response
        // API returns: {status: "success", data: {totalDocs: 3442, totalPages: 345}}
        const responseData = response?.data?.data || {};
        const totalCount =
          responseData?.totalDocs ??
          responseData?.totalCount ??
          responseData?.total ??
          responseData?.count ??
          response?.data?.totalDocs ??
          response?.data?.totalCount ??
          response?.data?.total ??
          response?.data?.count ??
          0;

        const parsedTotalCount = Number(totalCount) || 0;
        commit("SET_TOTAL_COUNT", { key, totalCount: parsedTotalCount });
        return parsedTotalCount;
      } catch (error) {
        // Handle aborted requests silently
        if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return 0;
        }
        console.error("[reportingUnitList] Failed to fetch total count:", error);
        return 0;
      }
    },

    async fetchReportingUnits(
      { state, commit, dispatch, rootState },
      { axiosInstance, filters = {}, perPage, reload = false, loadMore = false, page = null, limit = null }
    ) {
      const explicitLimit = Number(limit);
      const safePerPage = Math.max(
        1,
        Number.isFinite(explicitLimit) && explicitLimit > 0
          ? explicitLimit
          : Number(perPage) || varianceAnalysisConstants.REPORTING_UNIT.PAGE_SIZE
      );
      const key = buildCacheKey(filters);
      commit("SET_ACTIVE_KEY", key);

      const cachedNode = state.cache[key];
      const explicitPage = Number.isInteger(page) && Number(page) >= 0 ? Number(page) : null;
      const shouldFetch =
        reload || loadMore || explicitPage !== null || !cachedNode || !(cachedNode.data || []).length;

      if (!shouldFetch) {
        commit("SET_LAST_BATCH", { key, batch: [] });
        return;
      }

      const nextToken = state.requestToken + 1;
      commit("SET_REQUEST_TOKEN", nextToken);
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      
      // Create new AbortController and cancel any pending request
      const abortController = new AbortController();
      commit("SET_ABORT_CONTROLLER", abortController);
      const signal = abortController.signal;

      // Only reset node on reload, NOT on page navigation (we need to keep totalCount)
      if (reload || !cachedNode) {
        commit("RESET_NODE", key);
      } else {
        commit("ENSURE_NODE", key);
      }

      const pageToRequest =
        explicitPage !== null
          ? explicitPage
          : loadMore
            ? state.cache[key]?.paginationPage || 0
            : 0;

      try {
        // Check if we have cached total count
        const cachedTotalCount = state.cache[key]?.totalCount || 0;
        
        // Fetch count API only when we don't have cached count (initial load or reload)
        const needsCountFetch = cachedTotalCount === 0;
        let totalCountFromApi = cachedTotalCount;
        let response;


        if (needsCountFetch) {
          // Initial load: Call count API and data API in parallel
          const [countResult, dataResponse] = await Promise.all([
            dispatch("fetchTotalCount", { axiosInstance, filters, signal }),
            storeGet(
              axiosInstance, rootState,
              "/v3/varianceanalysis/analysis/getreportingunit_pagination/",
              { ...filters, limit: safePerPage, page: pageToRequest },
              signal ? { signal } : {}
            ),
          ]);
          totalCountFromApi = countResult;
          response = dataResponse;

          if (state.requestToken !== nextToken) {
            return;
          }

          if (!response || response.data?.status !== STATUS.SUCCESS) {
            throw new Error(
              response?.data?.message || MESSAGES.REPORTING_UNIT_FETCH_FAILED
            );
          }
        } else {
          // Page navigation: We have cached count, just fetch data for the requested page
          response = await storeGet(
            axiosInstance, rootState,
            "/v3/varianceanalysis/analysis/getreportingunit_pagination/",
            { ...filters, limit: safePerPage, page: pageToRequest },
            signal ? { signal } : {}
          );

          if (state.requestToken !== nextToken) {
            return;
          }

          if (!response || response.data?.status !== STATUS.SUCCESS) {
            throw new Error(
              response?.data?.message || MESSAGES.REPORTING_UNIT_FETCH_FAILED
            );
          }
        }

        const payloadData = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        // Prefer count from dedicated API, fallback to response or default
        const totalCountResponse =
          totalCountFromApi ||
          (response?.data?.totalCount ??
          response?.data?.total ??
          response?.data?.count ??
          response?.data?.totalRecords ??
          response?.data?.recordsTotal ??
          response?.data?.meta?.total ??
          response?.data?.pagination?.total ??
          0);

        const parsedTotalCount = Number(totalCountResponse) || 0;
        // Use total count from API, no fallback needed since we have count API
        const resolvedTotalCount = parsedTotalCount;

        let reportedPage = response?.data?.currentPage ?? response?.data?.page;
        let currentPageIndex = explicitPage !== null ? explicitPage : pageToRequest;

        if (typeof reportedPage === "number") {
          if (response?.data?.currentPage !== undefined && reportedPage >= 1) {
            currentPageIndex = reportedPage - 1;
          } else {
            currentPageIndex = reportedPage;
          }
        }

        if (currentPageIndex < 0) {
          currentPageIndex = 0;
        }

        // Calculate hasMore based on total count - if no total count, use data length heuristic
        const hasMore = resolvedTotalCount > 0
          ? (currentPageIndex + 1) * safePerPage < resolvedTotalCount
          : payloadData.length === safePerPage;


        // For server-side pagination: always replace data (don't accumulate)
        // Only append for infinite scroll (loadMore=true and no explicit page)
        if (loadMore && !reload && explicitPage === null) {
          commit("APPEND_TO_NODE", {
            key,
            data: payloadData,
            hasMore,
            totalCount: resolvedTotalCount,
            pageIndex: currentPageIndex,
          });
        } else {
          // Pagination mode: replace with current page data only
          commit("REPLACE_NODE", {
            key,
            data: payloadData,
            hasMore,
            totalCount: resolvedTotalCount,
            pageIndex: currentPageIndex,
          });
        }

        commit("SET_LAST_BATCH", {
          key,
          batch: payloadData,
          totalCount: resolvedTotalCount,
          pageIndex: currentPageIndex,
        });
      } catch (error) {
        // Handle aborted requests silently - don't show error
        if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          // Request was cancelled, don't update state or show error
          return;
        }
        if (state.requestToken !== nextToken) {
          return;
        }
        const message = error?.message || MESSAGES.REPORTING_UNIT_FETCH_FAILED;
        commit("SET_ERROR", message);
        commit("SET_LAST_BATCH", { key, batch: [] });
        commit("sbError", message, { root: true });
        throw error;
      } finally {
        if (state.requestToken === nextToken) {
          commit("SET_LOADING", false);
          // Clear the abort controller reference after request completes
          if (state.abortController === abortController) {
            commit("SET_ABORT_CONTROLLER", null);
          }
        }
      }
    },
    resetCacheForFilters({ commit }, filters = {}) {
      const key = buildCacheKey(filters);
      commit("RESET_NODE", key);
    },
  },

  getters: {
    getNodeByFilters: (state) => (filters = {}) => {
      const key = buildCacheKey(filters);
      const node = state.cache[key] || DEFAULT_NODE();
      return {
        data: [...(node.data || [])],
        paginationPage: node.paginationPage || 0,
        hasMore: Boolean(node.hasMore),
        lastBatch: [...(node.lastBatch || [])],
        totalCount: Number(node.totalCount) || 0,
        currentPage: Number(node.currentPage) || 0,
      };
    },
    getLastBatchByFilters: (state) => (filters = {}) => {
      const key = buildCacheKey(filters);
      return [...(state.cache[key]?.lastBatch || [])];
    },
    getCacheSnapshot: (state) => {
      const snapshot = {};
      Object.keys(state.cache || {}).forEach((key) => {
        const node = state.cache[key] || DEFAULT_NODE();
        snapshot[key] = {
          data: [...(node.data || [])],
          paginationPage: node.paginationPage || 0,
          hasMore: Boolean(node.hasMore),
          lastBatch: [...(node.lastBatch || [])],
          totalCount: Number(node.totalCount) || 0,
          currentPage: Number(node.currentPage) || 0,
        };
      });
      return snapshot;
    },
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
  },
};
